﻿import React, { useState, useEffect } from 'react';
import { apiCall, ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Patient as PatientType } from '../../types/patient';
import { formatDate, formatPhone } from './utils/patientUtils';

// Import Tab Components
import OverviewTab from './tabs/OverviewTab';
import ClinicalTab from './tabs/ClinicalTab';
import ImagingTab from './tabs/ImagingTab';
import DocumentsTab from './tabs/DocumentsTab';
import BillingTab from './tabs/BillingTab';
import PatientReferralHeaderInfo from './PatientReferralHeaderInfo';
import InterestedPartiesTab from './InterestedPartiesTab';

/**
 * Patient component - displays all patient information with tabs
 */
const Patient: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [patient, setPatient] = useState<PatientType | null>(null);
  const [loading, setLoading] = useState(true);
  
  const [activeTab, setActiveTab] = useState('overview');
  
  // Clinical State - initialize with empty arrays
  const [documents, setDocuments] = useState<any[]>([]);
  
  console.log('====== RENDER: Patient Component with ID:', id, '======');
  
  // Function to fetch patient data
  const fetchPatient = async () => {
    try {
      console.log(`🔍 Fetching patient with UUID: ${id}`);
      const response = await apiCall(`/patients/${id}/`, { method: 'GET' }, token || undefined);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Fetched patient data:', JSON.stringify(data));
        console.log('✅ Patient found!', data);
        setPatient(data);
      } else {
        console.error('❌ No patient found with UUID:', id);
      }
      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching patient:', error);
      setLoading(false);
    }
  };
  
  useEffect(() => {
    // Reset state when patient ID changes
    console.log('🔄 Effect triggered with ID:', id);
    setPatient(null);
    setLoading(true);
    
    fetchPatient();

    // Cleanup function
    return () => {
      console.log('🧹 Cleanup for ID:', id);
    };
  }, [id]); // Re-run the effect when id changes
  
  // Debug log whenever patient state changes
  useEffect(() => {
    console.log('👤 Patient state updated:', patient);
  }, [patient]);
  
  // Fetch documents and correspondence when patient is loaded
  useEffect(() => {
    if (patient?.id && token) {
      console.log('🔍 Fetching documents for patient ID:', patient.id);
      
      // Fetch documents using ENDPOINTS constant
      const params = new URLSearchParams();
      params.append('patient_id', patient.id.toString());
      const url = `${ENDPOINTS.DOCUMENTS.LIST}?${params.toString()}`;
      
      console.log('📄 Fetching from URL:', url);
      
      apiCall(url, { method: 'GET' }, token)
        .then(async (response) => {
          console.log('📄 Documents response status:', response.status, response.ok);
          if (response.ok) {
            const data = await response.json();
            console.log('📄 Fetched documents data:', data);
            console.log('📄 Documents results:', data.results);
            console.log('📄 Documents count:', (data.results || data || []).length);
            const documentsList = data.results || data || [];
            setDocuments(Array.isArray(documentsList) ? documentsList : []);
          } else {
            const errorText = await response.text();
            console.error('❌ Documents fetch failed:', response.status, errorText);
          }
        })
        .catch((error) => {
          console.error('❌ Error fetching documents:', error);
        });
      
      // Fetch correspondence
      apiCall(`/correspondence/letters/?patient_id=${patient.id}`, { method: 'GET' }, token)
        .then(async (response) => {
          if (response.ok) {
            const data = await response.json();
            console.log('✉️ Fetched correspondence:', data);
          }
        })
        .catch((error) => {
          console.error('❌ Error fetching correspondence:', error);
        });
    }
  }, [patient?.id, token]);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-info mr-4"></div>
        <div className="text-xl font-semibold">Loading patient data...</div>
      </div>
    );
  }
  
  if (!patient) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <svg className="h-16 w-16 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h2 className="text-xl font-bold mb-2">Patient Not Found</h2>
        <p className="text-base-content opacity-70 mb-4">We couldn't find the patient you're looking for.</p>
        <button 
          onClick={() => navigate('/patients')}
          className="px-4 py-2 btn btn-primary"
        >
          Return to Patient List
        </button>
      </div>
    );
  }
  
  // Get highest priority emergency contact
  const primaryEmergencyContact = patient.emergency_contacts?.sort((a, b) => a.priority - b.priority)[0];

  const deriveStateFromPostcode = (postcode?: string) => {
    const pc = Number((postcode || '').trim());
    if (!Number.isFinite(pc)) return '';

    if ((pc >= 1000 && pc <= 1999) || (pc >= 2000 && pc <= 2599) || (pc >= 2619 && pc <= 2899) || (pc >= 2921 && pc <= 2999)) return 'NSW';
    if ((pc >= 200 && pc <= 299) || (pc >= 2600 && pc <= 2618) || (pc >= 2900 && pc <= 2920)) return 'ACT';
    if ((pc >= 3000 && pc <= 3999) || (pc >= 8000 && pc <= 8999)) return 'VIC';
    if ((pc >= 4000 && pc <= 4999) || (pc >= 9000 && pc <= 9999)) return 'QLD';
    if (pc >= 5000 && pc <= 5799) return 'SA';
    if ((pc >= 6000 && pc <= 6797) || (pc >= 6800 && pc <= 6999)) return 'WA';
    if (pc >= 7000 && pc <= 7999) return 'TAS';
    if (pc >= 800 && pc <= 999) return 'NT';

    return '';
  };

  const resolvedState = (patient.state || '').trim().toUpperCase() || deriveStateFromPostcode(patient.postal_code);
  
  return (
    <div className="flex bg-base-200" style={{ height: 'calc(100vh - 140px)' }}>
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Patient Header - Fixed */}
        <div className="flex-shrink-0 p-6 pb-0">
          {/* Patient Header */}
          <div className="card bg-gradient-to-r from-primary to-secondary text-primary-content shadow-xl mb-6">
        <div className="card-body py-4">
          <div className="flex items-start justify-between gap-6">
            {/* Left Section: Patient Name & Info with Avatar */}
            <div className="flex items-start gap-4 flex-1">
              <div className="flex-shrink-0">
                <div className="h-16 w-16 bg-base-100/20 rounded-full flex items-center justify-center text-primary-content text-xl font-bold border-2 border-primary-content/30">
                  {patient.first_name.charAt(0)}{patient.last_name.charAt(0)}
                </div>
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  {patient.first_name} {patient.last_name}
                  <span className="badge badge-success badge-sm gap-2">
                    Active
                  </span>
                </h1>
                <div className="flex flex-wrap items-center text-sm opacity-90 mt-1 gap-4">
                  <span>MRN: {patient.display_id}</span>
                  <span>{formatDate(patient.date_of_birth)} ({patient.age} years)</span>
                  <span>{patient.gender === 'M' ? 'Male' : patient.gender === 'F' ? 'Female' : patient.gender}</span>
                </div>
                
                {/* Insurance Type Tags */}
                {patient.insurance && patient.insurance.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {patient.insurance.map((insurance, index) => (
                      <span 
                        key={insurance.id || index} 
                        className="badge badge-sm bg-base-100/20 text-primary-content border-primary-content/30"
                      >
                        {insurance.insurance_type_display || insurance.insurance_type}
                        {insurance.is_primary && " (Primary)"}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Center Section: Patient Details Grid */}
            <div className="grid grid-cols-3 gap-x-6 flex-1 text-sm">
              {/* Row 1: Medicare */}
              <div>
                <div className="text-xs opacity-70 uppercase tracking-wide mb-1 font-bold">Medicare</div>
                {patient.medicare ? (
                  <>
                    <div className="font-medium">{patient.medicare.medicare_number}-{patient.medicare.reference_number}</div>
                    <div className="text-xs opacity-80">Exp: {formatDate(patient.medicare.expiry_date)}</div>
                  </>
                ) : (
                  <div className="opacity-70">Not provided</div>
                )}
              </div>
              
              {/* Row 1: Contact */}
              <div>
                <div className="text-xs opacity-70 uppercase tracking-wide mb-1 font-bold">Contact</div>
                <div>{formatPhone(patient.contact_phone) || 'No phone'}</div>
                <div className="text-xs opacity-80 truncate">{patient.contact_email || 'No email'}</div>
              </div>
              
              {/* Row 1: Address */}
              <div>
                <div className="text-xs opacity-70 uppercase tracking-wide mb-1 font-bold">Address</div>
                <div>{patient.address_line1 || 'No address provided'}</div>
                <div className="text-xs opacity-80">
                  {[patient.city, resolvedState].filter(Boolean).join(' ')} {patient.postal_code || ''}
                </div>
              </div>
            </div>
            
            {/* Emergency Contact Section */}
            <div className="text-sm">
              <div className="text-xs opacity-70 uppercase tracking-wide mb-1 font-bold">Emergency Contact</div>
              {primaryEmergencyContact ? (
                <>
                  <div className="font-medium">
                    {primaryEmergencyContact.first_name} {primaryEmergencyContact.last_name}
                  </div>
                  <div className="text-xs opacity-80">
                    {primaryEmergencyContact.relationship || 'Not specified'} • {formatPhone(primaryEmergencyContact.mobile) || formatPhone(primaryEmergencyContact.phone) || 'No phone'}
                  </div>
                </>
              ) : (
                <div className="opacity-70">Not provided</div>
              )}
            </div>

            {/* Right Section: Action Buttons */}
            <div className="flex flex-col gap-2">
              <button className="btn btn-sm bg-base-100 text-base-content hover:bg-base-200">
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Edit Patient
              </button>
              <button className="btn btn-sm btn-outline text-primary-content border-primary-content hover:bg-primary-content hover:text-primary">
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
                More Options
              </button>
            </div>
          </div>

          {/* Referring Doctor / Interested Parties / Referral Status - own row
              underneath the main details so it doesn't compress the address/
              email into wrapping over other fields. */}
          <div className="mt-3 pt-3 border-t border-primary-content/20">
            <PatientReferralHeaderInfo
              patientId={patient.id}
              onViewInterestedParties={() => setActiveTab('interestedParties')}
            />
          </div>
        </div>
      </div>
      </div>

      {/* Tabs and Content - Flex grow */}
      <div className="flex-1 flex flex-col overflow-hidden px-6">
        {/* Tabs Navigation */}
        <div role="tablist" className="tabs tabs-bordered flex-shrink-0">
        <button
          role="tab"
          onClick={() => setActiveTab('overview')}
          className={`tab ${activeTab === 'overview' ? 'tab-active' : ''}`}
        >
          Overview
        </button>
        <button
          role="tab"
          onClick={() => setActiveTab('clinical')}
          className={`tab ${activeTab === 'clinical' ? 'tab-active' : ''}`}
        >
          Clinical
        </button>
        <button
          role="tab"
          onClick={() => setActiveTab('imaging')}
          className={`tab ${activeTab === 'imaging' ? 'tab-active' : ''}`}
        >
          Results
        </button>
        <button
          role="tab"
          onClick={() => setActiveTab('documents')}
          className={`tab ${activeTab === 'documents' ? 'tab-active' : ''}`}
        >
          Documents
        </button>
        <button
          role="tab"
          onClick={() => setActiveTab('billing')}
          className={`tab ${activeTab === 'billing' ? 'tab-active' : ''}`}
        >
          Billing
        </button>
        <button
          role="tab"
          onClick={() => setActiveTab('insurance')}
          className={`tab ${activeTab === 'insurance' ? 'tab-active' : ''}`}
        >
          Insurance
        </button>
        <button
          role="tab"
          onClick={() => setActiveTab('referrals')}
          className={`tab ${activeTab === 'referrals' ? 'tab-active' : ''}`}
        >
          Referrals
        </button>
        <button
          role="tab"
          onClick={() => setActiveTab('interestedParties')}
          className={`tab ${activeTab === 'interestedParties' ? 'tab-active' : ''}`}
        >
          Interested Parties
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 flex flex-col overflow-hidden mt-6">
        {activeTab === 'overview' && <OverviewTab patient={patient} />}
        {activeTab === 'clinical' && <ClinicalTab patient={patient} />}
        {activeTab === 'imaging' && <ImagingTab patient={patient} />}
        {activeTab === 'documents' && (
          <div className="h-full overflow-hidden">
            <DocumentsTab patient={patient} documents={documents} />
          </div>
        )}
        {activeTab === 'billing' && <BillingTab patient={patient} billing={[]} />}
        {activeTab === 'insurance' && (
          <div className="space-y-6">
            {/* Insurer Section - a single unified list, Insurer Type (Self-
                funded/Private/WorkCover/Transport/Military) replaces what
                used to be separate WorkCover/Transport/DVA claim sections */}
            <div className="bg-base-100 rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Insurer</h3>
                <div className="flex gap-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                    {patient.insurance?.length || 0} Active Policies
                  </span>
                </div>
              </div>

              {patient.insurance && patient.insurance.length > 0 ? (
                <div className="space-y-4">
                  {patient.insurance.map((insurance, index) => (
                    <div key={insurance.id || index} className="border border-accent rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-base-content opacity-60">Insurer Type</p>
                          <p className="mt-1">{insurance.insurance_type_display || insurance.insurance_type}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-base-content opacity-60">Provider</p>
                          <p className="mt-1">{insurance.provider_name || 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-base-content opacity-60">Policy Number</p>
                          <p className="mt-1">{insurance.policy_number || 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-base-content opacity-60">Membership Number</p>
                          <p className="mt-1">{insurance.membership_number || 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-base-content opacity-60">Valid Until</p>
                          <p className="mt-1">{insurance.expiry_date ? formatDate(insurance.expiry_date) : 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-base-content opacity-60">Status</p>
                          <p className="mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                            {insurance.is_primary ? 'Primary' : 'Active'}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 flex justify-end space-x-2">
                        <button className="px-3 py-1 text-sm font-medium text-blue-700 hover:text-blue-800">
                          Edit
                        </button>
                        <button className="px-3 py-1 text-sm font-medium text-red-700 hover:text-red-800">
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-base-content opacity-60">No insurance information available</p>
              )}

              <div className="mt-6 flex flex-wrap gap-2">
                <button className="btn btn-primary">
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Insurer
                </button>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'referrals' && (
          <div className="space-y-6">
            <div className="bg-base-100 rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Referral Information</h3>
                <button className="btn btn-primary">
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Referral
                </button>
              </div>
              {patient.referrals && patient.referrals.length > 0 ? (
                <div className="space-y-4">
                  {patient.referrals.map((referral, index) => (
                    <div key={referral.id || index} className="border border-accent rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-base-content opacity-60">Referrer</p>
                          <p className="mt-1">{referral.referrer_name || 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-base-content opacity-60">Date</p>
                          <p className="mt-1">{referral.referral_date ? formatDate(referral.referral_date) : 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-base-content opacity-60">Validity</p>
                          <p className="mt-1">{referral.days_remaining ? `${referral.days_remaining} days remaining` : 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-base-content opacity-60">Status</p>
                          <p className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            referral.is_valid ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                          }`}>
                            {referral.is_valid ? 'Valid' : 'Expired'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-base-content opacity-60">No referral information available</p>
              )}
            </div>
          </div>
        )}
        {activeTab === 'interestedParties' && (
          <InterestedPartiesTab patientId={patient.id} />
        )}
      </div>
      </div>
      </div>
    </div>
  );
};

export default Patient;
